import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import { Layout } from '@/components/Layout';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import OperatorModule from '@/pages/OperatorModule';
import IMFTModule from '@/pages/IMFTModule';
import BMFTModule from '@/pages/BMFTModule';
import MarketingModule from '@/pages/MarketingModule';
import StoreModule from '@/pages/StoreModule';
import FinanceModule from '@/pages/FinanceModule';
import AccountingModule from '@/pages/AccountingModule';
import HRModule from '@/pages/HRModule';
import OperationsModule from './pages/OperationsModule';
import Reports from '@/pages/Reports';
import CustomersPage from '@/pages/CustomersPage';
import CustomerProfile from '@/pages/CustomerProfile';
import CallCenterWifiReport from '@/pages/CallCenterWifiReport';
import ServiceOps from '@/pages/ServiceOps';
import NOCModule from '@/pages/NOCModule';
import FiberMap from '@/pages/FiberMap';
import MobileApp from '@/pages/MobileApp';
import CustomerUCP from '@/pages/CustomerUCP';
import UnifiedApp from '@/pages/UnifiedApp';
import GMDashboard from '@/pages/GMDashboard';
import AIManager from '@/pages/AIManager';
import PublicTicketSubmission from '@/pages/PublicTicketSubmission';
import ONUListPage from '@/pages/ONUListPage';
import AdminScheduleView from '@/pages/AdminScheduleView';
import OLTManagementPage, { MikroTikConfigurePage } from '@/pages/OLTManagementPage';
import NOCONUPage from '@/pages/NOCONUPage';
import ONUDetailPage from '@/pages/ONUDetailPage';
import TR069Page from '@/pages/TR069Page';
import SyncLogsPage from '@/pages/SyncLogsPage';
import ONUAuthorizationPage from '@/pages/ONUAuthorizationPage';
import ZonesPage from '@/pages/ZonesPage';
import ODBsPage from '@/pages/ODBsPage';
import ODBSearchPage from '@/pages/ODBSearchPage';
import ONUTypesPage from '@/pages/ONUTypesPage';
import SpeedProfilesPage from '@/pages/SpeedProfilesPage';
import AuthPresetsPage from '@/pages/AuthPresetsPage';
import NOCGeneralPage from '@/pages/NOCGeneralPage';

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[var(--md-surface)]"><div className="w-8 h-8 border-[3px] border-[var(--md-primary)] border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" />;
  return children;
};
const ROLE_DEFAULT = {
  SuperAdmin: '/dashboard', Admin: '/dashboard', HR: '/hr', Operator: '/operator', IMFT: '/imft', 'IMFT Supervisor': '/imft',
  BMFT: '/bmft', 'BMFT Supervisor': '/bmft', Marketing: '/marketing', 'Sales Manager': '/marketing',
  'Sales Staff': '/marketing', Finance: '/finance', Store: '/store', NOC: '/noc',
  'General Manager': '/gm-dashboard',
};
const SmartRedirect = () => {
  const { user } = useAuth();
  const target = ROLE_DEFAULT[user?.role] || '/dashboard';
  return <Navigate to={target} replace />;
};
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/submit-ticket" element={<PublicTicketSubmission />} />
          <Route path="/mobile" element={<MobileApp />} />
          <Route path="/customer" element={<CustomerUCP />} />
          <Route path="/app" element={<UnifiedApp />} />
          <Route path="/" element={<Protected><Layout /></Protected>}>
            <Route index element={<SmartRedirect />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="customers/call-center-report" element={<CallCenterWifiReport />} />
            <Route path="customers/:username" element={<CustomerProfile />} />
            <Route path="operator" element={<OperatorModule />} />
            <Route path="service-ops" element={<ServiceOps />} />
            <Route path="noc" element={<NOCModule />} />
            <Route path="imft" element={<IMFTModule />} />
            <Route path="bmft" element={<BMFTModule />} />
            <Route path="fiber-map" element={<FiberMap />} />
            <Route path="marketing" element={<MarketingModule />} />
            <Route path="store" element={<StoreModule />} />
            <Route path="finance" element={<FinanceModule />} />
            <Route path="accounting" element={<AccountingModule />} />
            <Route path="hr/*" element={<HRModule />} />
            <Route path="reports" element={<Reports />} />
            <Route path="gm-dashboard" element={<GMDashboard />} />
            <Route path="ai-manager" element={<AIManager />} />
            <Route path="onu-list" element={<ONUListPage />} />
            <Route path="admin-schedule" element={<AdminScheduleView />} />
            <Route path="noc-mikrotik" element={<MikroTikConfigurePage />} />
            <Route path="noc-olt" element={<OLTManagementPage />} />
            <Route path="noc-onu" element={<NOCONUPage />} />
            <Route path="noc-onu/:onu_id" element={<ONUDetailPage />} />
            <Route path="noc-tr069" element={<TR069Page />} />
            <Route path="noc-sync-logs" element={<SyncLogsPage />} />
            <Route path="noc-onu-auth" element={<ONUAuthorizationPage />} />
            <Route path="noc-zones" element={<ZonesPage />} />
            <Route path="noc-odbs" element={<ODBsPage />} />
            <Route path="noc-onu-types" element={<ONUTypesPage />} />
            <Route path="noc-speed-profiles" element={<SpeedProfilesPage />} />
            <Route path="noc-auth-presets" element={<AuthPresetsPage />} />
            <Route path="noc-general" element={<NOCGeneralPage />} />
            <Route path="odb-search" element={<ODBSearchPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
        <Toaster position="top-center" />
      </AuthProvider>
    </BrowserRouter>
  );
}
export default App;
